/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// UI lib components
import { notifications } from '@mantine/notifications';

// Lib
import { apiClient } from '@lib/api-client';
import { TMutationConfig } from '@lib/react-query';

// Types
import { TApiError } from '@global-types/api/api';

// Constants
import { queryKeys } from '@config/constants/query-keys';
import {
	TApiResponseProjectKpis,
	TBodyProjectKpis,
} from '@features/dashboard/types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const statisticsProjectKpis = (
	body: TBodyProjectKpis
): Promise<TApiResponseProjectKpis> => {
	return apiClient.post('/statistics/kpis/projects', {
		...body,
		withCredentials: true,
	});
};

type TUseProjectKpisOptions = {
	mutationConfig?: TMutationConfig<
		typeof statisticsProjectKpis
	>;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useProjectKpis = ({
	mutationConfig,
}: TUseProjectKpisOptions) => {
	const { t } = useTranslation();
	const { onSuccess } = mutationConfig || {};

	return useMutation({
		mutationFn: statisticsProjectKpis,
		mutationKey: [queryKeys.app.dashboard.kpis.key],
		onSuccess: (...args) => {
			onSuccess?.(...args);
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'error',
			});
		},
	});
};
